'use client';
import React from 'react';
import Link from 'next/link';
import { Dna, ArrowRight, Star, Target, Zap, TrendingUp } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

export default function BusinessBuilderHero() {
  const { t } = useLanguage();
  const bb = t?.businessBuilderSection;

  const STEPS = [
    { emoji: '💡', label: bb?.step1 ?? 'Enter your dream' },
    { emoji: '🧬', label: bb?.step2 ?? 'Dream DNA™ analysis' },
    { emoji: '📋', label: bb?.step3 ?? 'Full plan generated' },
    { emoji: '🚀', label: bb?.step4 ?? 'Launch with confidence' },
  ];

  const OUTCOMES = [
    { icon: Star, label: bb?.outcome1 ?? 'Dream Score™', color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
    { icon: Target, label: bb?.outcome2 ?? 'Brand Kit', color: 'text-primary', bg: 'bg-primary/10' },
    { icon: Zap, label: bb?.outcome3 ?? 'Launch Roadmap', color: 'text-accent', bg: 'bg-accent/10' },
    { icon: TrendingUp, label: bb?.outcome4 ?? 'Product Strategy', color: 'text-mint', bg: 'bg-mint/10' },
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] blob-primary opacity-10 pointer-events-none" />
      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="h-px flex-1 max-w-16 bg-gradient-to-r from-transparent to-primary/40" />
          <span className="text-xs font-700 uppercase tracking-widest text-primary px-3">{bb?.flagshipLabel ?? 'Flagship Methodology'}</span>
          <div className="h-px flex-1 max-w-16 bg-gradient-to-l from-transparent to-primary/40" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div>
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full glass-card border border-primary/20 mb-6">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Dna size={12} className="text-white" />
              </div>
              <span className="text-sm font-700 text-foreground">{t?.homePage?.businessBuilder ?? 'Dream DNA Studio™'}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent/15 text-accent font-700">{bb?.newBadge ?? 'Flagship'}</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-800 text-foreground mb-5 leading-tight">
              {bb?.headline ?? 'Discover the complete'}{' '}
              <span className="gradient-text">{bb?.headlineHighlight ?? 'DNA of your dream'}</span>
            </h2>

            <p className="text-base text-muted-foreground mb-8 leading-relaxed max-w-lg">
              {bb?.description ?? 'Dream DNA Studio™ analyzes the full DNA of your business idea — identifying strengths, weaknesses, opportunities, risks, business model, branding, ideal customer, monetization, and launch strategy.'}
            </p>

            <div className="flex items-center gap-3 mb-8 flex-wrap">
              {STEPS?.map((s, i) => (
                <React.Fragment key={s?.label}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{s?.emoji}</span>
                    <span className="text-xs font-600 text-muted-foreground">{s?.label}</span>
                  </div>
                  {i < STEPS?.length - 1 && (
                    <ArrowRight size={12} className="text-muted-foreground/40 shrink-0" />
                  )}
                </React.Fragment>
              ))}
            </div>

            <Link
              href="/dream-workspace"
              className="inline-flex items-center gap-3 btn-primary px-8 py-4 rounded-2xl text-base font-700 glow-pulse"
            >
              <Dna size={20} />
              {bb?.ctaBtn ?? 'Analyze My Dream DNA™'}
              <ArrowRight size={18} />
            </Link>

            <p className="text-xs text-muted-foreground mt-4">
              {bb?.ctaNote ?? 'Free to try · No credit card required · Full analysis in seconds'}
            </p>
          </div>

          {/* Right: Visual card */}
          <div className="relative">
            <div className="glass-card rounded-3xl p-6 border border-primary/20 shadow-xl shadow-primary/5">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Dna size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-700 text-foreground">{bb?.blueprintTitle ?? 'Dream DNA Studio™ Analysis'}</p>
                  <p className="text-xs text-muted-foreground">{bb?.blueprintSubtitle ?? 'Generated in seconds'}</p>
                </div>
                <span className="ml-auto text-[10px] px-2 py-1 rounded-full bg-mint/15 text-mint font-700">{bb?.readyBadge ?? 'Ready'}</span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-5">
                {OUTCOMES?.map((item) => {
                  const ItemIcon = item?.icon;
                  return (
                    <div key={item?.label} className={`flex items-center gap-2.5 p-3 rounded-xl ${item?.bg} border border-border`}>
                      <ItemIcon size={14} className={item?.color} />
                      <span className="text-xs font-600 text-foreground">{item?.label}</span>
                    </div>
                  );
                })}
              </div>

              <div className="p-4 rounded-2xl bg-muted/50 border border-border mb-4">
                <p className="text-[10px] font-600 text-muted-foreground uppercase tracking-wide mb-2">{bb?.sampleNameLabel ?? 'Sample Dream DNA™ Score'}</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 rounded-full bg-border overflow-hidden">
                    <div className="h-full w-4/5 rounded-full bg-gradient-to-r from-primary to-accent" />
                  </div>
                  <span className="text-lg font-800 text-foreground">82/100</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">High business potential · Low competition</p>
              </div>

              <div>
                <p className="text-[10px] font-600 text-muted-foreground uppercase tracking-wide mb-2">{bb?.actionPlanLabel ?? 'Dream DNA™ Insights'}</p>
                <div className="space-y-1.5">
                  {[
                    bb?.actionDay1 ?? '🧬 Strong recurring revenue potential identified',
                    bb?.actionDay2 ?? '🎯 Ideal customer profile: Women 28–45, health-focused',
                    bb?.actionDay3 ?? '🚀 Recommended model: Service + Digital Products',
                  ]?.map((action, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="w-4 h-4 rounded-full bg-primary/15 text-primary text-[9px] font-700 flex items-center justify-center shrink-0">
                        {i + 1}
                      </span>
                      {action}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="absolute -top-3 -right-3 px-3 py-1.5 rounded-full glass-card border border-accent/30 text-xs font-700 text-accent shadow-lg float-anim">
              🧬 {bb?.aiPoweredBadge ?? 'Dream DNA™'}
            </div>
            <div className="absolute -bottom-3 -left-3 px-3 py-1.5 rounded-full glass-card border border-mint/30 text-xs font-700 text-mint shadow-lg float-anim" style={{ animationDelay: '1s' }}>
              🚀 {bb?.launchReadyBadge ?? 'Launch-Ready'}
            </div>
          </div>
        </div>

        <div className="mt-20 mb-2 flex items-center gap-4">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs font-600 uppercase tracking-widest text-muted-foreground px-4">{bb?.individualToolsLabel ?? 'Additional AI Studios'}</span>
          <div className="h-px flex-1 bg-border" />
        </div>
      </div>
    </section>
  );
}
